import { LayoutDashboard, Diamond, Target, Megaphone, Users, Image, Bell, MessageSquare, BarChart3, DollarSign, FileSpreadsheet, Rocket, Brain, FileText, Shield, LogOut, ClipboardList, UserSearch, SendHorizonal, Wallet, ChevronDown, BookOpen, Activity, Package, Link2, BadgeDollarSign, Landmark, Send, BookOpenText } from 'lucide-react';
import { NavLink } from '@/components/NavLink';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { useState, useRef, useEffect, useCallback, type ComponentProps } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubItem,
  SidebarMenuSubButton,
  SidebarFooter,
  useSidebar,
} from '@/components/ui/sidebar';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Button } from '@/components/ui/button';

const topNavItems = [
  { title: 'Dashboard', url: '/', icon: LayoutDashboard },
  { title: 'Hidden Gems', url: '/hidden-gems', icon: Diamond },
  { title: 'Challenges', url: '/challenges', icon: Target },
];

const baseNavItems = [
  { title: 'Users', url: '/users', icon: Users },
  { title: 'Brand Credits', url: '/brand-credits', icon: BadgeDollarSign },
  { title: 'Submissions', url: '/submissions', icon: Image },
  { title: 'Notifications', url: '/notifications', icon: Bell },
  { title: 'Deep Link Generator', url: '/link-generator', icon: Link2 },
  { title: 'Coach Guide', url: '/coach-guide', icon: BookOpenText },
  { title: 'Support Tickets', url: '/feedback', icon: MessageSquare },
  { title: 'Analytics', url: '/analytics', icon: BarChart3 },
  { title: 'Revenue', url: '/revenue', icon: DollarSign },
  { title: 'TAP Data', url: '/tap-data', icon: FileSpreadsheet },
  { title: 'CAP Data', url: '/cap-data', icon: FileSpreadsheet },
  { title: 'CAP Applications', url: '/cap-applications', icon: ClipboardList },
  { title: 'Weekly Top 100', url: '/weekly-top-100', icon: Rocket },
  ...(import.meta.env.VITE_ENV === 'production' ? [
    { title: 'Brand Intelligence', url: '/brand-intelligence', icon: Brain },
    { title: 'Brief Requests', url: '/brief-requests', icon: FileText },
  ] : []),
  { title: 'Trybe Outreach', url: '/trybe-outreach', icon: Send },
  { title: 'Audit Log', url: '/audit-log', icon: Shield },
  { title: 'Brand Activity', url: '/brand-activity', icon: Activity },
];

// On mobile the sidebar is an overlay sheet, so navigating away has to dismiss
// it — otherwise the tapped page renders behind a sheet that stays open. Every
// in-sidebar link goes through this so a tap on the already-active route closes
// it too (a location effect alone would not, since the path never changes).
function SidebarNavLink({ onClick, ...props }: ComponentProps<typeof NavLink>) {
  const { isMobile, setOpenMobile } = useSidebar();

  return (
    <NavLink
      {...props}
      onClick={(event) => {
        onClick?.(event);
        if (isMobile) setOpenMobile(false);
      }}
    />
  );
}

export function AppSidebar() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isCampaignsActive = location.pathname.startsWith('/campaigns') || location.pathname === '/hybrid-ledger';
  const [campaignsOpen, setCampaignsOpen] = useState(isCampaignsActive);

  // Which edges of the nav list have content beyond them. Drives the fade in
  // .sidebar-scroll so a clipped list reads as scrollable, not truncated.
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScroll, setCanScroll] = useState({ up: false, down: false });

  const syncScrollEdges = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const { scrollTop, scrollHeight, clientHeight } = el;
    // 1px tolerance — fractional scroll offsets never settle on an exact 0 or
    // scrollHeight - clientHeight, which would leave a fade stuck on.
    setCanScroll({
      up: scrollTop > 1,
      down: scrollTop + clientHeight < scrollHeight - 1,
    });
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    syncScrollEdges();
    // Watch the viewport (container resize) and the groups (the Campaigns
    // collapsible and async badges change content height after mount).
    const observer = new ResizeObserver(syncScrollEdges);
    observer.observe(el);
    for (const child of Array.from(el.children)) observer.observe(child);
    return () => observer.disconnect();
  }, [syncScrollEdges]);

  const { data: pendingCapCount } = useQuery({
    queryKey: ['cap-applications-pending-count'],
    queryFn: async () => {
      const { count } = await supabase
        .from('cap_applications')
        .select('id', { count: 'exact', head: true })
        .eq('status', 'pending');
      return count ?? 0;
    },
    refetchInterval: 60000,
  });

  const { data: pendingSampleRequestsCount } = useQuery({
    queryKey: ['sample-requests-pending-count'],
    queryFn: async () => {
      const { count } = await supabase
        .from('tiktok_sample_applications')
        .select('id', { count: 'exact', head: true })
        .ilike('status', '%pending%');
      return count ?? 0;
    },
    refetchInterval: 60000,
  });

  const { data: newFeedbackCount } = useQuery({
    // Tickets waiting on US: no chat thread yet, or the customer sent the most
    // recent message. That's the number a support team acts on — a plain count
    // of open tickets includes every thread already mid-conversation. The flag
    // is a computed column on user_feedback_with_user, so this stays an exact
    // head count with no rows fetched (mirrors the CAP Applications badge).
    queryKey: ['support-awaiting-count'],
    queryFn: async () => {
      const { count } = await supabase
        .from('user_feedback_with_user')
        .select('id', { count: 'exact', head: true })
        .eq('awaiting_reply', true);
      return count ?? 0;
    },
    refetchInterval: 60000,
  });

  const { data: unpaidCompletedHiresCount } = useQuery({
    queryKey: ['creator-payments-unpaid-completed-count'],
    queryFn: async () => {
      // Completed hires awaiting payout. Mirror the Creator Payments page,
      // which treats a null payment_status as unpaid. Head-only exact count —
      // no rows fetched, matching the CAP Applications badge.
      const { count } = await supabase
        .from('cr_hires')
        .select('id', { count: 'exact', head: true })
        .eq('status', 'completed')
        .or('payment_status.eq.unpaid,payment_status.is.null');
      return count ?? 0;
    },
    refetchInterval: 60000,
  });

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  console.log('Pending CAP Applications:', pendingCapCount);
  return (
    <Sidebar>
      <div className="p-4 border-b border-sidebar-border">
        <h2 className="text-lg font-bold text-sidebar-foreground">BMC Admin</h2>
        <p className="text-xs text-sidebar-foreground/60">Brands Meet Creators</p>
      </div>
      <SidebarContent
        ref={scrollRef}
        onScroll={syncScrollEdges}
        className="sidebar-scroll"
        data-can-scroll-up={canScroll.up}
        data-can-scroll-down={canScroll.down}
      >
        <SidebarGroup>
          <SidebarGroupLabel>Management</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {topNavItems.map((item) => (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton asChild>
                    <SidebarNavLink
                      to={item.url}
                      end={item.url === '/'}
                      className="flex items-center gap-3 px-3 py-2 rounded-md text-sidebar-foreground/70 hover:text-sidebar-foreground hover:bg-sidebar-accent transition-colors"
                      activeClassName="bg-sidebar-accent text-sidebar-foreground font-medium"
                    >
                      <item.icon className="h-4 w-4 shrink-0" />
                      <span className="flex-1">{item.title}</span>
                    </SidebarNavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}

              {/* Campaigns — collapsible with sub-items */}
              <Collapsible open={campaignsOpen} onOpenChange={setCampaignsOpen}>
                <SidebarMenuItem>
                  <CollapsibleTrigger asChild>
                    <SidebarMenuButton
                      className={`flex items-center gap-3 px-3 py-2 rounded-md transition-colors w-full ${
                        isCampaignsActive
                          ? 'bg-sidebar-accent text-sidebar-foreground font-medium'
                          : 'text-sidebar-foreground/70 hover:text-sidebar-foreground hover:bg-sidebar-accent'
                      }`}
                    >
                      <Megaphone className="h-4 w-4 shrink-0" />
                      <span className="flex-1">Campaigns</span>
                      <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${campaignsOpen ? 'rotate-180' : ''}`} />
                    </SidebarMenuButton>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <SidebarMenuSub>
                      <SidebarMenuSubItem>
                        <SidebarMenuSubButton asChild>
                          <SidebarNavLink
                            to="/campaigns"
                            end
                            className="flex items-center gap-2 px-3 py-1.5 rounded-md text-sidebar-foreground/70 hover:text-sidebar-foreground hover:bg-sidebar-accent transition-colors text-sm"
                            activeClassName="text-sidebar-foreground font-medium"
                          >
                            <Megaphone className="h-3.5 w-3.5 shrink-0" />
                            Campaigns
                          </SidebarNavLink>
                        </SidebarMenuSubButton>
                      </SidebarMenuSubItem>
                      <SidebarMenuSubItem>
                        <SidebarMenuSubButton asChild>
                          <SidebarNavLink
                            to="/hybrid-ledger"
                            end
                            className="flex items-center gap-2 px-3 py-1.5 rounded-md text-sidebar-foreground/70 hover:text-sidebar-foreground hover:bg-sidebar-accent transition-colors text-sm"
                            activeClassName="text-sidebar-foreground font-medium"
                            title="Review Video Submissions"
                          >
                            <BookOpen className="h-3.5 w-3.5 shrink-0" />
                            <span>Review Video Submissions</span>
                          </SidebarNavLink>
                        </SidebarMenuSubButton>
                      </SidebarMenuSubItem>
                    </SidebarMenuSub>
                  </CollapsibleContent>
                </SidebarMenuItem>
              </Collapsible>

              {baseNavItems.map((item) => (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton asChild>
                    <SidebarNavLink
                      to={item.url}
                      end={item.url === '/'}
                      className="flex items-center gap-3 px-3 py-2 rounded-md text-sidebar-foreground/70 hover:text-sidebar-foreground hover:bg-sidebar-accent transition-colors"
                      activeClassName="bg-sidebar-accent text-sidebar-foreground font-medium"
                    >
                      <item.icon className="h-4 w-4 shrink-0" />
                      <span className="flex-1">{item.title}</span>
                      {item.title === 'Support Tickets' && !!newFeedbackCount && newFeedbackCount > 0 && (
                        <span className="ml-auto inline-flex items-center justify-center rounded-full bg-yellow-500 text-white text-[10px] font-bold h-5 min-w-5 px-1">
                          {newFeedbackCount}
                        </span>
                      )}
                      {item?.title === 'CAP Applications' && !!pendingCapCount && pendingCapCount > 0 && (
                        <span className="ml-auto inline-flex items-center justify-center rounded-full bg-yellow-500 text-white text-[10px] font-bold h-5 min-w-5 px-1">
                          {pendingCapCount}
                        </span>
                      )}
                    </SidebarNavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        <SidebarGroup>
          <SidebarGroupLabel>Finance</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              <SidebarMenuItem>
                <SidebarMenuButton asChild>
                  <NavLink
                    to="/accounts-finance"
                    className="flex items-center gap-3 px-3 py-2 rounded-md text-sidebar-foreground/70 hover:text-sidebar-foreground hover:bg-sidebar-accent transition-colors"
                    activeClassName="bg-sidebar-accent text-sidebar-foreground font-medium"
                  >
                    <Landmark className="h-4 w-4 shrink-0" />
                    <span className="flex-1">Accounts & Finance</span>
                  </NavLink>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        <SidebarGroup>
          <SidebarGroupLabel>Creator Roster</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              <SidebarMenuItem>
                <SidebarMenuButton asChild>
                  <SidebarNavLink
                    to="/roster-campaigns"
                    className="flex items-center gap-3 px-3 py-2 rounded-md text-sidebar-foreground/70 hover:text-sidebar-foreground hover:bg-sidebar-accent transition-colors"
                    activeClassName="bg-sidebar-accent text-sidebar-foreground font-medium"
                  >
                    <Megaphone className="h-4 w-4 shrink-0" />
                    <span className="flex-1">Jobs</span>
                  </SidebarNavLink>
                </SidebarMenuButton>
              </SidebarMenuItem>
              <SidebarMenuItem>
                <SidebarMenuButton asChild>
                  <SidebarNavLink
                    to="/creator-payments"
                    className="flex items-center gap-3 px-3 py-2 rounded-md text-sidebar-foreground/70 hover:text-sidebar-foreground hover:bg-sidebar-accent transition-colors"
                    activeClassName="bg-sidebar-accent text-sidebar-foreground font-medium"
                  >
                    <Wallet className="h-4 w-4 shrink-0" />
                    <span className="flex-1">Creator Payments</span>
                    {!!unpaidCompletedHiresCount && unpaidCompletedHiresCount > 0 && (
                      <span className="ml-auto inline-flex items-center justify-center rounded-full bg-yellow-500 text-white text-[10px] font-bold h-5 min-w-5 px-1">
                        {unpaidCompletedHiresCount}
                      </span>
                    )}
                  </SidebarNavLink>
                </SidebarMenuButton>
              </SidebarMenuItem>
              <SidebarMenuItem>
                <SidebarMenuButton asChild>
                  <SidebarNavLink
                    to="/campaign-ops"
                    className="flex items-center gap-3 px-3 py-2 rounded-md text-sidebar-foreground/70 hover:text-sidebar-foreground hover:bg-sidebar-accent transition-colors"
                    activeClassName="bg-sidebar-accent text-sidebar-foreground font-medium"
                  >
                    <SendHorizonal className="h-4 w-4 shrink-0" />
                    <span className="flex-1">Job Operations</span>
                  </SidebarNavLink>
                </SidebarMenuButton>
              </SidebarMenuItem>
              <SidebarMenuItem>
                <SidebarMenuButton asChild>
                  <SidebarNavLink
                    to="/sample-requests"
                    className="flex items-center gap-3 px-3 py-2 rounded-md text-sidebar-foreground/70 hover:text-sidebar-foreground hover:bg-sidebar-accent transition-colors"
                    activeClassName="bg-sidebar-accent text-sidebar-foreground font-medium"
                  >
                    <Package className="h-4 w-4 shrink-0" />
                    <span className="flex-1">Sample Requests</span>
                    {!!pendingSampleRequestsCount && pendingSampleRequestsCount > 0 && (
                      <span className="ml-auto inline-flex items-center justify-center rounded-full bg-yellow-500 text-white text-[10px] font-bold h-5 min-w-5 px-1">
                        {pendingSampleRequestsCount}
                      </span>
                    )}
                  </SidebarNavLink>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        <SidebarGroup>
          <SidebarGroupLabel>Hiring</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              <SidebarMenuItem>
                <SidebarMenuButton asChild>
                  <SidebarNavLink
                    to="/hiring"
                    end
                    className="flex items-center gap-3 px-3 py-2 rounded-md text-sidebar-foreground/70 hover:text-sidebar-foreground hover:bg-sidebar-accent transition-colors"
                    activeClassName="bg-sidebar-accent text-sidebar-foreground font-medium"
                  >
                    <UserSearch className="h-4 w-4 shrink-0" />
                    <span className="flex-1">Exclusive Campaigns</span>
                  </SidebarNavLink>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
      <SidebarFooter className="p-4 border-t border-sidebar-border">
        <Button variant="ghost" className="w-full justify-start text-sidebar-foreground/70 hover:text-sidebar-foreground hover:bg-sidebar-accent" onClick={handleLogout}>
          <LogOut className="mr-2 h-4 w-4" /> Sign Out
        </Button>
      </SidebarFooter>
    </Sidebar>
  );
}
